
import ColorTemperatureHelp from '@/components/ColorTemperatureHelp.vue'
import ColorTemperatureSwatch from '@/components/ColorTemperatureSwatch.vue'
import ProductTechnicalFilterSlider from '@/components/ProductTechnicalFilterSlider.vue'
import TechnicalFilterHelp from '@/components/TechnicalFilterHelp.vue'
import {
  colorTemperatureToneLabel,
  colorTemperatureToneRanges,
} from '@/utils/colorTemperature'
import {
  formatTechnicalNumber,
  formatTechnicalValue,
  snapToStep,
  technicalRangeShortSummary,
} from '@/utils/technicalFilters'

export default {
  name: 'ProductTechnicalFilterPanel',
  components: {
    ColorTemperatureHelp,
    ColorTemperatureSwatch,
    ProductTechnicalFilterSlider,
    TechnicalFilterHelp,
  },
  props: {
    filter: {
      type: Object,
      required: true,
    },
    layout: {
      type: String,
      default: 'menu',
      validator: value => ['menu', 'sheet'].includes(value),
    },
    footer: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      range: [...this.filter.value],
      touched: false,
    }
  },
  computed: {
    isColorTemperature() {
      return this.filter.key === 'color_temperature'
    },
    isExplained() {
      return ['watts', 'lumens'].includes(this.filter.key)
    },
    heading() {
      return this.$t(
        `components.productTechnicalFilters.${this.filter.i18n}Heading`
      )
    },
    helpButtonClass() {
      return this.layout === 'sheet'
        ? 'focus-ring !tw-w-11 !tw-h-11 !tw-ml-0 -tw-mr-2 !tw-rounded-full hover:tw-bg-slate-100'
        : 'focus-ring !tw-w-11 !tw-h-11 !tw-ml-0 -tw-my-1 -tw-mr-2 !tw-rounded-full hover:tw-bg-slate-100'
    },
    sliderValue() {
      return this.range.map(number =>
        snapToStep(number, this.filter.bounds, this.filter.step)
      )
    },
    hasSelection() {
      return this.filter.active || this.touched
    },
    tones() {
      if (!this.isColorTemperature) return []

      const formatNumber = number =>
        formatTechnicalNumber(number, this.filter, this.$i18n.locale)

      return colorTemperatureToneRanges(
        this.filter.bounds,
        this.filter.values
      ).map(tone => ({
        ...tone,
        label: colorTemperatureToneLabel(tone.nominal, formatNumber),
      }))
    },
    selectedTone() {
      if (!this.hasSelection) return null

      const tone = this.tones.find(
        ({ from, to }) => from === this.range[0] && to === this.range[1]
      )

      return tone ? tone.key : null
    },
    valueText() {
      return technicalRangeShortSummary(
        this.sliderValue,
        this.filter,
        this.$i18n.locale
      )
    },
  },
  watch: {
    'filter.value'(value) {
      this.range = [...value]
      this.touched = false
    },
  },
  methods: {
    update(value) {
      if (value.every((number, index) => number === this.sliderValue[index])) {
        return
      }

      this.range = [...value]
      this.touched = true
    },
    selectTone(tone) {
      this.range = [tone.from, tone.to]
      this.touched = true
    },
    format(value) {
      return formatTechnicalValue(value, this.filter, this.$i18n.locale)
    },
    apply() {
      this.$emit('apply', this.filter, [...this.range])
    },
    reset() {
      this.$emit('reset', this.filter)
    },
  },
}
